<template>
  <div class="flex w-full relative bg-gray-50 dark:bg-navy-1000">
    <SiteTrackSideBar
      @select="selectSideBarOption"
    />
    <!-- ACTIVE SITE TRACK PAGE -->
    <div
      id="siteTrackScrollable"
      class="flex flex-col w-full bg-gray-50 dark:bg-navy-1000"
      @dragover="drag"
      ref="activePage"
    >
      <!-- SITE TRACK HEADER -->
      <div class="flex-col items-center flex">
        <div
          class="sectionHeader flex items-center justify-center w-full z-20 lg:z-15 lg:transition-all duration-200 transform"
          :class="headerScrolled"
        >
          <SiteTrackLogo class="transition-all duration-200" :class="logoClass" />
        </div>
      </div>
      <div v-if="readyToLoad" :class="pageClass">
        <!-- SELECTED SITE TRACK COMPONENT -->
        <component :is="currentViewComponent" :key="currentViewComponent" />
      </div>
      <div v-else class="w-full h-full">
        <LoadingSpinnerPage />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState, mapActions } from "vuex"
import { defineAsyncComponent } from "vue"

const PreventativeMaintenance = defineAsyncComponent({
  loader: () => import('../../Site-Track/components/Preventative_Maintenance/PreventativeMaintenance.vue'),
  loadingComponent: LoadingSpinnerPage,
})
const CorrectiveMaintenance = defineAsyncComponent({
  loader: () => import('../../Site-Track/components/Corrective_Maintenance/CorrectiveMaintenance.vue'),
  loadingComponent: LoadingSpinnerPage,
})
const IncidentTracking = defineAsyncComponent({
  loader: () => import('../../Site-Track/components/Incident_Tracking/IncidentTracking.vue'),
  loadingComponent: LoadingSpinnerPage,
})
const InventoryTracking = defineAsyncComponent({
  loader: () => import('../../Site-Track/components/Inventory/InventoryTracking.vue'),
  loadingComponent: LoadingSpinnerPage,
})
const AssetView = defineAsyncComponent({
  loader: () => import('../../Site-Track/components/Assets/AssetView.vue'),
  loadingComponent: LoadingSpinnerPage,
})
const PurchaseOrders = defineAsyncComponent({
  loader: () => import('../../Site-Track/components/PurchaseOrders/PurchaseOrders.vue'),
  loadingComponent: LoadingSpinnerPage,
})
const OrderReceipts = defineAsyncComponent({
  loader: () => import('../../Site-Track/components/PurchaseOrders/OrderReceipts.vue'),
  loadingComponent: LoadingSpinnerPage,
})
const ManagerTools = defineAsyncComponent({
  loader: () => import('../../Site-Track/components/ManagerTools/ManagerTools.vue'),
  loadingComponent: LoadingSpinnerPage,
})
const InventoryView = defineAsyncComponent({
  loader: () => import('./Inventory/InventoryView.vue'),
  loadingComponent: LoadingSpinnerPage,
})
const SiteTrackDashboard = defineAsyncComponent({
  loader: () => import("./Dashboard/SiteTrackDashboard.vue"),
  loadingComponent: LoadingSpinnerPage,
})
import LoadingSpinnerPage from "@/components/Loading/LoadingSpinner/LoadingSpinnerPage.vue"
import SiteTrackSideBar from '../../Site-Track/components/SiteTrackSideBar.vue'
import SiteTrackLogo from "@/assets/Cruz_Control_Site_Track_Logo_Full_Color.svg"
import { getAllLocations } from "../../Site-Track/services/SiteData"
import { getAllAssets, getAllAssetModels } from "../../Site-Track/services/Assets"
import { getAllProcedures, getMixedWorkOrdersQuery } from "../../Site-Track/services/PreventativeService"
import { getVendors } from "../../Site-Track/services/PurchaseOrders"
import { getEmployeeQuery } from "../../Site-Track/services/ManagerService"
import { getMyActiveTimers } from "../../Site-Track/services/Timers"
import { getAllSiteChemicalData, getAllChemTargetsGlobal } from "../../Site-Scan/services/Chemical"
import { getSTWashPackages } from "../../Site-Track/services/IncidentService"
import { getImagesBySources } from "@/services/images.service"
import { Chart, registerables } from "chart.js"
import "chartjs-adapter-date-fns"

const registry = {
  "Preventative Maintenance": PreventativeMaintenance,
  "Corrective Maintenance": CorrectiveMaintenance,
  "Incident Tracking": IncidentTracking,
  "Assets": AssetView,
  "Manager Tools": ManagerTools,
  "InventoryView": InventoryView, // special case flag
  "InventoryTracking": InventoryTracking,
  "PurchaseOrders": PurchaseOrders,
  "OrderReceipts": OrderReceipts,
  "Dashboard": SiteTrackDashboard,
}

export default {
  name: "SiteTrack",
  components: {
    SiteTrackSideBar,
    LoadingSpinnerPage,
    SiteTrackLogo
  },
  computed: {
    ...mapState(["headers", "screen", "orientation"]),
    ...mapGetters([
      "getSiteId",
      "showingModuleHeader",
      "atTopOfPage",
      "getIsTenantSuperuser",
      "getUserManagementAccess",
      "getPermissionSet",
      "getUserPermissions",
      "loadedGlobalValues",
      "STPreloadData",
      "siteAssets",
      "getProcedures",
      "siteSuppliers",
      "getAssetModels",
      "getLocations",
      "currentlyRunningPMTimer",
      "currentlyRunningCMTimer",
      "getEmployeeSites",
      "getEmployeesUserId"
    ]),
    siteId() {
      return this.headers.siteId
    },
    currentViewComponent() {
      if (!this.readyToLoad) return null

      if (this.path.includes("Inventory") && !this.isChemicalStandalone) {
        return registry["InventoryTracking"]
      }
      if (this.$route.path.includes("purchase_orders")) {
        return registry["PurchaseOrders"]
      }
      if (this.$route.path.includes("receipts")) {
        return registry["OrderReceipts"]
      }
      if (this.isChemicalStandalone) {
        return registry["InventoryView"]
      }
      return registry[this.path] || registry["Dashboard"]
    },
    needLoad() {
      // MARK IF PATH REQUIRES STATE VALUES PRELOAD
      return {
        "Site Track": [
          "preloadData",
        ],
        "Preventative Maintenance": [
          "preloadData",
          "assets",
          "procedures"
        ],
        "Corrective Maintenance": [
          "preloadData",
          "assets",
          "procedures"
        ],
        "Incident Tracking": [
          "preloadData"
        ],
        "Inventory": [
          "preloadData"
        ],
        "Purchasing": [
          "preloadData",
          "employee_sites",
          "suppliers"
        ],
        "Manager Tools": [
          "preloadData",
          "models"
        ]
      }
    },
    readyToLoad() {
      if (!this.needLoad[this.path]) return true
      for (let needToLoad of this.needLoad[this.path]) {
        if (!this.loadedGlobalValues[needToLoad]) return false
      }
      return true
    },
    logoClass() {
      if (this.atTopOfPage && this.screen.lg) return "h-14 lg:h-20 py-2 my-0"
      else return "h-14 lg:h-16 py-2" 
    },
    headerScrolled() {
      if (this.isMobileLandscape && !this.isTablet && !this.screen.xl) return "hidden"
      let className = "md:fixed lg:shadow-md bg-gray-50 dark:bg-navy-1000 lg:bg-white-off "
      if (this.atTopOfPage || !this.screen.lg) {
        className += "h-16 lg:h-24 "
      }
      else className += "shadow-lg h-24 lg:h-16 "
      if (!this.showingModuleHeader && this.screen.lg) className += "-translate-y-full md:translate-y-0 "
      else className += "-translate-y-0 "
      return className
    },
    pageClass() {
      let classes = "flex flex-col relative "
      if (!this.isMobileLandscape || (this.isTablet)) classes += "md:mt-16 lg:mt-24 "
      if (this.path === "Corrective Maintenance") classes += "items-center "
      else classes += "items-start "
      return classes
    },
    path() {
      return this.$route.name
    },
    isMobile() {
      return /Mobi/i.test(window.navigator.userAgent)
    },
    isTablet() {
      return /ipad|tablet/i.test(window.navigator.userAgent.toLowerCase())
    },
    isMobileLandscape() {
      return this.isMobile && this.orientation.landscape
    },
    isMobilePortrait() {
      return this.isMobile && this.orientation.portrait
    },
    isChemicalStandalone() {
      return this.getPermissionSet.is_chemical_only
    }
  },
  watch: {
    path(val) {
      this.setSTPage(val)
    },
    siteId(val, oldVal) {
      if (val !== oldVal) {
        this.loadSiteData()
      }
    }
  },
  mounted() {
    this.showModuleHeader(true)
    this.loadSiteData()
    this.loadSiteScanData()
  },
  methods: {
    ...mapMutations([
      "setSTPage",
      "setPrompt",
      "showModuleHeader",
      "setRunningPMTimer",
      "setRunningCMTimer",
      "setActiveContainers",
      "setInactiveContainers",
      "setChemGroups",
      "setConsumableCategories",
      "setChemFormulas",
      "setChemicals",
      "setChemSensors",
      "setGlobalTanks",
      "setWashPackages",
      "setAllEmployeeAvatars"
    ]),
    ...mapActions([
      "setSiteAssets",
      "setAssetModels",
      "setLocations",
      "setSuppliers",
      "setProcedures",
      "setFullEmployeeData"
    ]),
    loadSiteData() {
      this.loadAssets()
      this.loadAssetModels()
      this.loadLocations()
      this.loadProcedures()
      this.loadVendors()
      this.loadEmployees()
      this.loadTimers()
      this.loadWashPackages()
    },
    loadAssets() {
      let assetAccess = this.getIsTenantSuperuser
        || this.getUserPermissions.site_track.assets?.assets?.main_view?.object?.read_bool
      if (!assetAccess) return
      getAllAssets(this.headers)
      .then(response => {
        if (response.status === 200) {
          this.setSiteAssets(response.data.results)
        }
        else {
          console.log(response)
          if (response.status !== 403) {
            this.setPrompt(["Could not load all data for this site!", "loadAssets", null, { header: "Error" }])
          }
        }
      }).catch(error => {
        console.log(error)
        this.setPrompt(["Could not load all data for this site!", "loadAssets", null, { header: "Error" }])
      })
    },
    loadAssetModels() {
      let assetAccess = this.getIsTenantSuperuser
        || this.getUserPermissions.site_track.manager_tools?.view_edit_procedures?.main_view?.object?.read_bool
      if (!assetAccess) {
        this.setAssetModels([])
        return
      }
      getAllAssetModels(this.headers)
      .then((response) => {
        if (response?.status === 200) {
          let models = {}
          for (let asset of response.data.results) {
            models[asset.id] = asset.name
          }
          this.setAssetModels(models)
        }
        else {
          console.log(response)
          if (response.status !== 403) {
            this.setPrompt(["Could not load all data for this site!", "loadAssetModels", null, { header: "Error" }])
          }
        }
      }, (error) => {
        console.log(error)
        this.setPrompt(["Could not load all data for this site!", "loadAssetModels", null, { header: "Error" }])
      })
    },
    loadLocations() {
      getAllLocations(this.headers)
      .then(response => {
        if (response.status === 200) {
          this.setLocations(response.data.results)
        }
        else {
          console.log(response)
          if (response.status !== 403) {
            this.setPrompt(["You do not have the required permissions to access location data for this site.", "closePrompt", null, { header: "Error" }])
          }
          else this.setPrompt(["Could not load all data for this site!", "loadLocations", null, { header: "Error" }])
        }
      }).catch(error => {
        console.log(error)
        this.setPrompt(["Could not load all data for this site!", "loadLocations", null, { header: "Error" }])
      })
    },
    loadProcedures() {
      getAllProcedures(this.headers)
      .then(response => {
        if (response.status === 200) this.setProcedures(response.data.results)
        else {
          console.log(response)
          if (response.status !== 403) {
            this.setPrompt(["Could not load all data for this site!", "loadProcedures", null, { header: "Error" }])
          }
        }
      }).catch(error => {
        console.log(error)
        this.setPrompt(["Could not load all data for this site!", "loadProcedures", null, { header: "Error" }])
      })
    },
    loadVendors() {
      let assetAccess = 
        this.getIsTenantSuperuser
        || 
        this.getUserPermissions.site_track.purchasing?.purchase_orders?.main_view?.object?.read_bool
        ||
        this.getUserPermissions.site_track.purchasing?.order_receipts?.main_view?.object?.read_bool
      if (!assetAccess) {
        this.setSuppliers([])
        return
      }
      getVendors(this.headers)
      .then(response => {
        if (response.status === 200) {
          this.setSuppliers(response.data.results)
        }
        else {
          console.log(response)
          if (response.status !== 403) {
            this.setPrompt(["Could not load vendor data for this site!", "loadVendors", null, { header: "Error" }])
          }
        }
      }).catch(error => {
        console.log(error)
        this.setPrompt(["Could not load vendor data for this site!", "loadVendors", null, { header: "Error" }])
      })
    },
    loadEmployees() {
      let employeeAccess = 
        this.getIsTenantSuperuser
        ||
        // will need to replace with new employee endpoint not just for managers
        this.getUserPermissions.site_track?.manager_tools?.employee_management?.main_view?.object?.read_bool
      if (!employeeAccess) {
        this.setFullEmployeeData([])
        return
      }
      let query = {
        page: -1,
      }
      getEmployeeQuery(query, this.headers)
      .then(response => {
        if (response?.status === 200) {
          this.setFullEmployeeData(response.data.results)
          this.loadAllEmployeeAvatars(response.data.results)
        }
        else {
          console.log(response)
          this.setPrompt(["Could not load all data for this site!", "loadEmployees", null, { header: "Error" }])
        }
      }, (error) => {
        console.log(error)
        this.setPrompt(["Could not load all data for this site!", "loadEmployees", null, { header: "Error" }])
      })
    },
    loadAllEmployeeAvatars(employees) {
      if (Object.keys(this.getEmployeesUserId).length === 0) return
      let userIds = []
      for (let user of employees) {
        if (user.user_id) userIds.push(user.user_id)
      }
      getImagesBySources(userIds, this.headers)
      .then(response => {
        if (response.status === 200) {
          this.setAllEmployeeAvatars(response.data.results)
        }
        else console.log(response)
      }).catch(error => console.log(error))
    },
    loadTimers() {
      // need to know which are the running PM and CM timers
      let timersAccess = this.getIsTenantSuperuser
        || this.getUserPermissions.site_track?.preventive_maintenance?.all_work_orders?.main_view?.object?.read_bool
        || this.getUserPermissions.site_track?.corrective_maintenance?.all_work_orders?.main_view?.object?.read_bool
      if (!timersAccess) {
        this.setRunningPMTimer(null)
        this.setRunningCMTimer(null)
        return
      }
      getMyActiveTimers(this.headers)
      .then(async response => {

        if (response.status === 200) {

          let runningTimers = response.data.results.filter(x => !x.paused)

          // should only be 2 timers max (1 PM and 1 CM)
          // check which one is which
          for (let timer of runningTimers) {
            await getMixedWorkOrdersQuery({ id: timer.work_order_id, _not_status: "canceled" }, this.headers)
            .then(async response => {
              if (response.status === 200) {
                let workOrder = response.data.results[0]
                if (!workOrder) return
                if (workOrder.type === "corrective") this.setRunningCMTimer(timer)
                else this.setRunningPMTimer(timer)
              }
              else console.log(response)
            }).catch(error => console.log(error))
          }
        }
        else {
          console.log(response)
          this.setRunningPMTimer(null)
          this.setRunningCMTimer(null)
          this.setPrompt(["Could not load all data for this site!", "loadTimers", null, { header: "Error" }])
        }
      }, (error) => {
        console.log(error)
        this.setRunningPMTimer(null)
        this.setRunningCMTimer(null)
        this.setPrompt(["Could not load all data for this site!", "loadTimers", null, { header: "Error" }])
      })
    },
    async loadSiteScanData() {
      // load site scan data to properly display threshold data for auto-generated work orders
      await this.loadChemicalMonitoringData()
      this.loadChemTargetsGlobal()
    },
    loadChemicalMonitoringData() {
      return new Promise(resolve =>
        getAllSiteChemicalData(this.getSiteId, this.headers)
          .then(async response => {
            if (response.status === 200 && response.data.results) {
              this.setActiveContainers(response.data.results.active_containers)
              this.setInactiveContainers(response.data.results.inactive_containers)
              this.setChemGroups(response.data.results.groups)
              this.setConsumableCategories(response.data.results.consumable_categories)
              this.setChemFormulas(response.data.results.formulas)
              this.setChemicals(response.data.results.chemicals)
              this.setChemSensors(response.data.results.sensors)
            }
            else {
              console.log(response)
            }
            resolve(response?.status)
          }).catch(error => {
            console.log(error)
            resolve(null)
        })
      )
    },
    async loadChemTargetsGlobal() {
      await getAllChemTargetsGlobal(this.headers)
      .then(response => {
        if (response.status === 200) {
          this.setGlobalTanks(response.data?.results?.sensor_targets)
        }
        else {
          console.log(response)
          this.setPrompt(["Could not load data for all tanks!", "loadChemTargetsGlobal", null, { header: "Error" }])
        }
      }).catch(error => {
        console.log(error)
        this.setPrompt(["Could not load data for all tanks!", "loadChemTargetsGlobal", null, { header: "Error" }])
      })
    },
    loadWashPackages() {
      getSTWashPackages(this.headers)
      .then(response => {
        if (response.status === 200) {
          this.setWashPackages(response.data.results)
        }
        else {
          console.warn(response)
        }
      }).catch(error => {
        console.warn(error)
      })
    },
    selectSideBarOption(option) {
      this.setSTPage(option)
    },
    drag() {
      if (event.clientY < 150) {
        this.$refs.activePage.scrollTop = this.$refs.activePage.scrollTop - 4
      }
      if (event.clientY > window.innerHeight - 50) {
        this.$refs.activePage.scrollTop = this.$refs.activePage.scrollTop + 2
      }
    },

    // Chart Bullshit
    resizeCanvas(chartContainer) {
         return chartContainer.offsetWidth
    },
    getWorkOrderData() {
            // return Array.from({length: 40}, () => Math.floor(Math.random() * 40));
            return {"Monday": Math.floor(Math.random() * 40),"Tuesday": Math.floor(Math.random() * 40), "Wednesday": Math.floor(Math.random() * 40), "Thursday": Math.floor(Math.random() * 40), "Friday": 1, "Saturday" : 0, "Sunday" : 0}
    },
    partsConsumedPerAsset() {
      return {"Conveyor": 2, "Bulldog": 6, "Top Wheel": 0,  "Crossover Wraps": 6, "Axis Arch": 1, "Sudz A Lot": 0, "High Boys": 0, "Dry N' Shine": 12}
    },

    workOrdersCompleted(container, chartName){
        Chart.register(...registerables)
        const chartContainer = document.getElementById(container)
        const ctx = document.getElementById(chartName)
        this.myChart = new Chart(ctx, {
            type: "line",
            data: {
                datasets: [{
                    label: "Work Orders Completed",
                    data: this.getWorkOrderData(),
                    fill: false,
                    borderColor: "rgb(75, 192, 192)",
                    tension: 0.1
                }]
            },
            
        });
        ctx.width = this.resizeCanvas(chartContainer)
        this.myChart.data.datasets[0].data = this.getWorkOrderData()
        this.myChart.update()
    },
    inventoryConsumed(container, chartName){
        Chart.register(...registerables)
        const chartContainer = document.getElementById(container)
        const ctx = document.getElementById(chartName)
        this.myChart = new Chart(ctx, {
            type: "bar",
            data: {
                datasets: [{
                    label: "Parts Consumed Per Asset",
                    data: this.partsConsumedPerAsset(),
                    fill: false,
                    borderColor: "rgb(75, 192, 192)",
                    tension: 0.1
                }]
            },
            
        });
        ctx.width = this.resizeCanvas(chartContainer)
        this.myChart.data.datasets[0].data = this.partsConsumedPerAsset()
        this.myChart.update()
    },
  },
}
</script>